<script setup>
import { supabase } from '@/supabase';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import AppMenuItem from './AppMenuItem.vue';

const router = useRouter();
const { t, locale } = useI18n();

// Available languages
const languages = [
    { label: 'English', value: 'en' },
    { label: 'हिन्दी', value: 'hi' },
    { label: 'मराठी', value: 'mr' },
    { label: 'ಕನ್ನಡ', value: 'kn' },
    { label: 'ગુજરાતી', value: 'gu' }
];

//  Language change handler
const changeLanguage = (event) => {
    locale.value = event.target.value;
    localStorage.setItem('lang', locale.value);
};

// ---------------------------
// Auth / Role setup
// ---------------------------
const selectedManagerId = ref(null);
const userKey = ref(localStorage.getItem('userKey'));
const role = ref(localStorage.getItem('role'));
const userId = Number(localStorage.getItem('userId')); // logged-in user

watch(userKey, (newValue) => localStorage.setItem('userKey', newValue));
watch(role, (newValue) => localStorage.setItem('role', newValue));

// ---------------------------
// Switch Handlers
// ---------------------------
const switchToParticipant = () => {
    userKey.value = '2';
    router.push('/participantDailyQuizes');
};

const switchToEducator = () => {
    userKey.value = '1';
    router.push('/educatordash');
};

const goToEducatorRoleBase = (id) => {
    router.push(`/educatordashrole/${id}`);
};

// ---------------------------
// Fetch Manager List
// ---------------------------
const managerList = ref([]);

const fetchManagers = async () => {
    const { data: managedUsersData, error: managerError } = await supabase
        .from('quiz_managers')
        .select('user_id')
        .eq('manager_id', Number(localStorage.getItem('user_id')));

    if (managerError) {
        console.error('Error fetching managed users:', managerError);
        return;
    }

    const uniqueUserIds = [...new Set(managedUsersData.map((item) => item.user_id))];

    if (uniqueUserIds.length === 0) {
        managerList.value = [];
        return;
    }

    const { data: usersData, error: usersError } = await supabase.from('users').select('id, name').in('id', uniqueUserIds);

    if (usersError) {
        console.error('Error fetching user names:', usersError);
        return;
    }

    managerList.value = usersData.map((user) => ({
        id: user.id,
        name: user.name
    }));
};

import { useRoute } from 'vue-router';

const route = useRoute()

const isDailyQuizActive = computed(() => {
    return [
        '/ParticipantDailyQuizes',
        '/dailyparticularquiz',
        '/GetDailyQuizes'
    ].some(path => route.path.startsWith(path));
});
onMounted(() => {
    // Load saved language from localStorage
    const savedLang = localStorage.getItem('lang');
    if (savedLang && languages.some(l => l.value === savedLang)) {
        locale.value = savedLang;
    }

    if (userKey.value === '1') {
        // fetchManagers();
    }

    setTimeout(() => {
        const dailyQuizzesToggle = document.querySelector('.daily-quizzes-menu .layout-submenu-toggler');
        if (dailyQuizzesToggle) {
            dailyQuizzesToggle.click(); // Simulate click to open
        }
    }, 100);
});

// ---------------------------
// Sidebar Menu Model
// ---------------------------
const model = computed(() => {
    selectedManagerId.value;
    if (userKey.value === '2' && role.value !== 'educator') {
        return [
            {
                label: t('home'),
                items: [
                    {
                        label: t('dailyQuizzes'),
                        icon: 'pi pi-calendar',
                        expanded: true,
                        class: 'daily-quizzes-menu',
                        isDailyQuiz: true,
                        isActiveParent: isDailyQuizActive.value,
                        items: [
                            {
                                label: t('quizes'), icon: 'pi pi-pencil', to: '/ParticipantDailyQuizes',
                                activeRoutes: [
                                    '/ParticipantDailyQuizes',
                                    '/dailyparticularquiz/:id'
                                ]
                            },
                            { label: t('dailyQuizLeaderboard'), icon: 'pi pi-trophy', to: '/GetDailyQuizes' }
                        ]
                    },
                    {
                        label: t('sansthaQuiz'),
                        icon: 'pi pi-building',
                        expanded: true,
                        items: [
                            { label: t('quizes'), icon: 'pi pi-pencil', to: '/SansthaQuiz' },
                            { label: t('sansthaQuizLeaderboard'), icon: 'pi pi-trophy', to: '/GetSansthaQuizes' }
                        ]
                    },
                    { label: t('generalQuizzes'), icon: 'pi pi-id-card', to: '/userdash' }
                ]
            }
        ];
    } else if (userKey.value === '2' && role.value === 'educator') {
        return [
            {
                label: t('home'),
                items: [
                    {
                        label: t('dailyQuizzes'),
                        icon: 'pi pi-calendar',
                        expanded: true,
                        class: 'daily-quizzes-menu',
                        isDailyQuiz: true,
                        isActiveParent: isDailyQuizActive.value,
                        items: [
                            {
                                label: t('quizes'), icon: 'pi pi-pencil', to: '/ParticipantDailyQuizes',
                                activeRoutes: [
                                    '/ParticipantDailyQuizes',
                                    '/dailyparticularquiz/:id'
                                ]
                            },
                            { label: t('dailyQuizLeaderboard'), icon: 'pi pi-trophy', to: '/GetDailyQuizes' }
                        ]
                    },
                    {
                        label: t('sansthaQuiz'),
                        icon: 'pi pi-building',
                        expanded: true,
                        items: [
                            { label: t('quizes'), icon: 'pi pi-pencil', to: '/SansthaQuiz' },
                            { label: t('sansthaQuizLeaderboard'), icon: 'pi pi-trophy', to: '/GetSansthaQuizes' }
                        ]
                    },
                    { label: t('generalQuizzes'), icon: 'pi pi-id-card', to: '/userdash' }
                ]
            },
            {
                items: [
                    {
                        label: t('switchToEducator'),
                        icon: 'pi pi-user',
                        class: 'text-green-600 font-semibold',
                        command: () => switchToEducator()
                    }
                ]
            }
        ];
    } else if (userKey.value === '3' && role.value === 'admin') {
        return [
            {
                label: t('home'),
                items: [
                    { label: t('adminDashboard'), icon: 'pi pi-id-card', to: '/admindash' },
                    { label: t('addBrandOrSponsorImage'), icon: 'pi pi-image', to: '/brand-sponsership' },
                    {
                        label: t('analyticsOverview'),
                        icon: 'pi pi-chart-bar',
                        items: [
                            {
                                label: t('overallAnalytics'),
                                icon: 'pi pi-chart-line',
                                to: '/analytics-overview'
                            },
                            {
                                label: t('quizAnalytics'),
                                icon: 'pi pi-book',
                                to: '/quiz-analytics'
                            }
                        ]
                    }
                ]
            }
        ];
    } else {
        const items = [
            {
                items: [
                    { label: t('educatorDashboard'), icon: 'pi pi-id-card', to: '/educatordash' },
                    { label: 'General Quiz', icon: 'pi pi-chart-line', to: '/QuizDashboard' },
                    { label: t('questionBank'), icon: 'pi pi-book', to: '/QuestionBank' },
                    { label: 'Sanstha General Quiz', icon: 'pi pi-users', to: '/SansthaQuizEdu' },
                    // { label: t('dailyQuizCreation'), icon: 'pi pi-calendar-plus', to: '/dailyQuizes' },
                    {
                        label: t('dailyQuizCreation'),
                        icon: 'pi pi-calendar-plus',
                        to: '/dailyQuizes',
                        activeRoutes: [
                            '/dailyQuizes',
                            '/chooseTypeQuiz',
                            '/createDailyQuiz',
                            '/createDailyQuiz/:id',
                            '/quizscoringmanagement/:id'
                        ]
                    },
                    { label: t('leaderboard'), icon: 'pi pi-trophy', to: '/getleadershipboard' },
                    // { label: t('sendMessages'), icon: 'pi pi-envelope', to: '/listTempletes' },
                    // { label: t('manageUsers'), icon: 'pi pi-user-edit', to: '/manage-users' },
                    // { label: t('roles'), icon: 'pi pi-shield', to: '/role-management' },
                    { label: t('assignLuckyWinners'), icon: 'pi pi-gift', to: '/luckywinners' },
                    {
                        label: t('settings'),
                        icon: 'pi pi-cog',
                        items: [
                            {
                                label: t('leaderboardPublishing'),
                                icon: 'pi pi-book',
                                to: '/leaderboard-publishing'
                            },
                            {
                                label: t('profile'),
                                icon: 'pi pi-user',
                                to: '/profile'
                            }
                        ]
                    }
                ]
            }
        ];

        if (managerList.value.length > 0) {
            items.push({
                label: t('managedByMe'),
                icon: 'pi pi-fw pi-briefcase',
                items: managerList.value.map((m) => ({
                    label: m.name,
                    icon: 'pi pi-user',
                    command: () => {
                        selectedManagerId.value = m.id;
                        goToEducatorRoleBase(m.id);
                    },
                    class: selectedManagerId.value === m.id ? 'text-green-600 font-semibold' : ''
                }))
            });
        }

        items.push({
            items: [
                {
                    label: t('switchToParticipant'),
                    icon: 'pi pi-user',
                    class: 'text-green-600 font-semibold',
                    command: () => switchToParticipant()
                }
            ]
        });

        return items;
    }
});
</script>

<template>
    <div>
        <!-- Language Dropdown -->
        <div class="p-4">
            <label class="text-sm font-semibold mr-2">🌐 {{ t('language') }}:</label>
            <select v-model="locale" @change="changeLanguage"
                class="border rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
                <option v-for="lang in languages" :key="lang.value" :value="lang.value">
                    {{ lang.label }}
                </option>
            </select>
        </div>

        <!-- Sidebar Menu -->
        <ul class="layout-menu">
            <template v-for="(item, i) in model" :key="i">
                <app-menu-item v-if="!item.separator" :item="item" :index="i" />
                <li v-if="item.separator" class="menu-separator"></li>
            </template>
        </ul>
    </div>
</template>
<style scoped>
/* Hide toggle arrow ONLY for Daily Quizzes */
:deep(.daily-quizzes-menu .pi-angle-down) {
    display: none !important;
}

:deep(.daily-quizzes-menu .layout-submenu-toggler) {
    display: none !important;
}

/* Force Daily Quizzes submenu to always be OPEN */
:deep(.daily-quizzes-menu > .layout-submenu) {
    display: block !important;
    max-height: unset !important;
    overflow: visible !important;
}

/* Ensure it stays visible even with inline styles */
:deep(.daily-quizzes-menu ul.layout-submenu) {
    display: block !important;
}

:deep(.active-daily-parent .layout-menuitem-text) {
    font-weight: 700 !important;
}

:deep(.active-daily-parent .layout-menuitem-icon) {
    font-weight: 700 !important;
}
</style>